import { presentIncomingShare } from './incoming-share-image';

const INCOMING_SHARE_PROTOCOL = 'portfoplus:';
const INCOMING_SHARE_HOST = 'incoming-share';

const IMAGE_MIME_TYPES = new Set(['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/heic', 'image/heif']);

const canonicalMimeType = mimeType => {
  const normalized = String(mimeType || '')
    .split(';')[0]
    .trim()
    .toLowerCase();
  if (normalized === 'image/jpg') {
    return 'image/jpeg';
  }
  return normalized;
};

const isImageMimeType = mimeType => IMAGE_MIME_TYPES.has(canonicalMimeType(mimeType));

const fileName = (name, fallback) => {
  const trimmed = typeof name === 'string' ? name.trim() : '';
  return trimmed || fallback;
};

const normalizeTextItem = item => {
  const text = typeof item.text === 'string' ? item.text : '';
  if (!text.trim()) {
    return null;
  }
  return { kind: 'text', text };
};

const normalizeFileItem = (item, kind, mimeType, fallbackName) => {
  if (typeof item.uri !== 'string' || !item.uri) {
    return null;
  }
  return {
    kind,
    uri: item.uri,
    name: fileName(item.name, fallbackName),
    mimeType
  };
};

export const normalizeIncomingShareItem = item => {
  if (!item || typeof item !== 'object') {
    return null;
  }

  const mimeType = canonicalMimeType(item.mimeType);
  const kind = item.kind;

  if (kind === 'text' || kind === 'url' || mimeType === 'text/plain' || mimeType === 'text/uri-list') {
    return normalizeTextItem(item);
  }

  if (kind === 'image' || isImageMimeType(mimeType)) {
    if (!isImageMimeType(mimeType)) {
      return null;
    }
    return normalizeFileItem(item, 'image', mimeType, 'image');
  }

  if (kind === 'pdf' || mimeType === 'application/pdf') {
    if (mimeType !== 'application/pdf') {
      return null;
    }
    return normalizeFileItem(item, 'pdf', 'application/pdf', 'document.pdf');
  }

  return null;
};

export const normalizeIncomingShare = share => {
  if (!share || typeof share.id !== 'string' || !share.id.trim()) {
    return null;
  }

  const items = (Array.isArray(share.items) ? share.items : []).map(normalizeIncomingShareItem).filter(Boolean);
  if (!items.length) {
    return null;
  }

  return { id: share.id, items };
};

export const isIncomingShareUrl = url => {
  if (typeof url !== 'string' || !url) {
    return false;
  }

  try {
    const parsed = new URL(url);
    return parsed.protocol === INCOMING_SHARE_PROTOCOL && parsed.hostname === INCOMING_SHARE_HOST;
  } catch (error) {
    return false;
  }
};

export const shouldRouteDeepLink = url => !isIncomingShareUrl(url);

export const hasIncomingShareSession = state =>
  !!(state && state.auth && state.auth.initialAuthDone && state.auth.isAuthenticated);

/** Screens the user sees before the signed-in app. `/` is handled separately. */
const PRE_AUTH_PREFIXES = [
  '/login',
  '/signup',
  '/userType',
  '/clientSignUp',
  '/verify_user',
  '/verify_Adviser',
  '/forgot_password',
  '/reset_password',
  '/dem',
  '/get-portfoplus',
  '/change_password',
  '/pa-signup'
];

const matchesPrefix = (pathname, prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`);

/** `/home`, `/c-dashboard`, and every other screen past login. */
export const isAuthedShareScreen = pathname => {
  if (typeof pathname !== 'string' || !pathname || pathname === '/') {
    return false;
  }
  return !PRE_AUTH_PREFIXES.some(prefix => matchesPrefix(pathname, prefix));
};

/** The shortcut stays hidden until login has finished and the app screen is up. */
export const canPresentIncomingShare = (state, pathname) =>
  hasIncomingShareSession(state) && isAuthedShareScreen(pathname);

/**
 * Opens the share dialog. A newer share replaces the one on screen.
 * Dropped shares are returned so the caller can release their files.
 */
export const handleIncomingShare = share => {
  if (!share || !share.id) {
    return [];
  }
  const dropped = presentIncomingShare(share);
  return Array.isArray(dropped) ? dropped : [];
};

export const createIncomingShareInbox = ({
  getPending,
  addListener,
  subscribeSession,
  subscribeActive,
  hasSession,
  onShare,
  onDismiss
}) => {
  const seen = new Set();
  const waiting = new Map();
  let started = false;

  const deliver = share => {
    const normalized = normalizeIncomingShare(share);
    if (!normalized || seen.has(normalized.id)) {
      return;
    }

    if (!hasSession()) {
      waiting.set(normalized.id, normalized);
      return;
    }

    seen.add(normalized.id);
    waiting.delete(normalized.id);
    onShare(normalized);
  };

  const pull = async () => {
    try {
      const pending = await getPending();
      const shares = pending && Array.isArray(pending.shares) ? pending.shares : [];
      shares.forEach(deliver);
    } catch (error) {
      // An older binary has no inbox plugin. The app still launches.
    }
  };

  const flush = () => {
    if (!hasSession()) {
      return;
    }
    const pending = Array.from(waiting.values());
    waiting.clear();
    if (!pending.length) {
      return;
    }
    // Several files can arrive before login. Only the latest shortcut is shown.
    pending.slice(0, -1).forEach(share => {
      seen.add(share.id);
      if (onDismiss) {
        onDismiss(share);
      }
    });
    deliver(pending[pending.length - 1]);
  };

  const start = async () => {
    if (started) {
      return;
    }
    started = true;
    subscribeSession(flush);
    // A custom icon makes the launcher alias the task root. The app can then
    // come forward without the share event, so read the cache on every resume.
    if (subscribeActive) {
      subscribeActive(pull);
    }
    await addListener(deliver);
    await pull();
    flush();
  };

  return { start, deliver, flush, pull };
};
